import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import bifoldProfiles from './bifold-profiles.json';
import swingProfiles from './swing-profiles.json';
const profiles = { ...bifoldProfiles, ...swingProfiles };
import { member } from './fixedWindows';
import { dividerArray } from '../configurator/dividerArray';

export const BIFOLD_PARTS = { head: '315387', sill: '315386', jamb: '315385', sash: '315396' };
const JAMB = .030, SILL = .048, HEAD = .063, GAP = .004, MAX_ANGLE = THREE.MathUtils.degToRad(84);
export const parseLayout = layout => (layout || '3+0').split('+').map(Number);

const extrusions = new Map();
function shapesFor(code, flip) {
  const key = `${code}:${flip}`;
  if (!extrusions.has(key)) extrusions.set(key, buildShapes(code, flip).map(c => ({ role: c.role, shapes: new THREE.ExtrudeGeometry(c.shapes, { depth: 1, steps: 1, bevelEnabled: false, curveSegments: 3 }) })));
  return extrusions.get(key);
}
function buildShapes(code, flip) {
  const data = profiles[code], h = data.height_mm;
  const v = ([x, y]) => new THREE.Vector2(x / 1000, (flip ? h - y : y) / 1000);
  return data.components.map(c => ({ role: c.role, shapes: c.shapes.map(s => {
    const shape = new THREE.Shape(s.outer.map(v));
    s.holes.forEach(ring => shape.holes.push(new THREE.Path(ring.map(v))));
    return shape;
  }) }));
}

export function assemble(parent, parts, width, height, materialByRole) {
  const grouped = {};
  for (const [code, sides, flip] of parts) {
    const offset = profiles[code].depth_mm / 2000;
    for (const c of shapesFor(code, flip)) for (const side of sides) {
      const g = member(c.shapes, side, width, height); g.translate(0, 0, -offset);
      (grouped[c.role] ||= []).push(g);
    }
  }
  for (const [role, geometries] of Object.entries(grouped)) {
    const mesh = new THREE.Mesh(mergeGeometries(geometries, false), materialByRole[role] || materialByRole.frame);
    geometries.forEach(g => g.dispose());
    mesh.castShadow = true; mesh.receiveShadow = true; mesh.userData.profileRole = role;
    parent.add(mesh);
  }
}

export function makePanel(width, height, materialByRole, glass, handle, dividers, sashCode = BIFOLD_PARTS.sash) {
  const inset = profiles[sashCode].height_mm > 60 ? .1 : .08;
  const pivot = new THREE.Group(), body = new THREE.Group();
  assemble(body, [[sashCode, ['sill', 'head', 'left', 'right'], false]], width, height, materialByRole);
  const pane = new THREE.Mesh(new THREE.BoxGeometry(width - inset, height - inset, .028), glass);
  pane.position.y = height / 2; pane.receiveShadow = true; pane.name = 'bifold-glass'; body.add(pane);
  if (dividers) dividerArray(body, 1, width, height, dividers, materialByRole.frame, { glassWidth: width - inset, glassHeight: height - inset, glassCenterY: height / 2 })([{ x: 0, z: 0 }]);
  if (handle) {
    const grip = new THREE.Mesh(new THREE.BoxGeometry(.018, .32, .03), materialByRole.frame);
    grip.position.set(width / 2 - .03, 1.05 - SILL, .055); grip.castShadow = true; grip.name = 'bifold-handle'; body.add(grip);
  }
  body.position.x = width / 2; pivot.add(body);
  pivot.userData.body = body;
  return pivot;
}

export function makeBifoldDoor(product, materials, options = {}) {
  const width = options.width ?? product.width, height = options.height ?? product.height;
  const [left, right] = parseLayout(options.layout || product.default_layout), count = left + right;
  const group = new THREE.Group(), frame = options.frame || materials.makeFrame();
  const materialByRole = { frame, thermal: materials.thermal, gasket: materials.gasket };
  assemble(group, [[BIFOLD_PARTS.sill, ['sill'], false], [BIFOLD_PARTS.head, ['head'], true], [BIFOLD_PARTS.jamb, ['left', 'right'], false]], width, height, materialByRole);
  const clear = width - 2 * JAMB, pw = (clear - GAP * (count + 1)) / count, ph = height - SILL - HEAD + .01;
  const stacks = [[...Array(left)].map((_, i) => ({ side: -1, i })), [...Array(right)].map((_, i) => ({ side: 1, i }))];
  const panels = stacks.flat().map(({ side, i }) => {
    const lead = side < 0 ? i === left - 1 : i === right - 1;
    const pivot = makePanel(pw, ph, materialByRole, materials.glass, lead, options.dividers);
    pivot.position.y = SILL - .005; group.add(pivot);
    return { pivot, side, i };
  });
  const animate = fraction => {
    const theta = Math.min(1, Math.max(0, fraction)) * MAX_ANGLE;
    for (const { pivot, side, i } of panels) {
      // Zig-zag chain from the jamb: even panels swing outward, odd panels return.
      const startX = side * (clear / 2 - GAP) - side * i * (pw + GAP * (1 - fraction)) * Math.cos(theta);
      const startZ = (i % 2) * -pw * Math.sin(theta);
      const dx = -side * Math.cos(theta), dz = (i % 2 ? 1 : -1) * Math.sin(theta);
      pivot.position.x = startX; pivot.position.z = startZ;
      pivot.rotation.y = Math.atan2(-dz, dx);
    }
    group.userData.panelPositions = panels.map(p => ({ x: +p.pivot.position.x.toFixed(4), z: +p.pivot.position.z.toFixed(4) }));
    group.userData.foldAngle = +THREE.MathUtils.radToDeg(theta).toFixed(1);
  };
  animate(options.fraction || 0);
  group.userData.bifold = true; group.userData.layout = `${left}+${right}`; group.userData.panelWidth = pw;
  group.userData.panelsLeft = panels.filter(p => p.side < 0).length; group.userData.panelsRight = panels.filter(p => p.side > 0).length;
  group.userData.panelPivots = panels.map(p => p.pivot);
  group.userData.animate = animate; group.userData.pickTargets = group.children;
  group.traverse(o => { if (o.isMesh) o.userData.productId = product.id; });
  return { group, frame, animate, progress: 0, raised: 0, panelWidth: pw, panelHeight: ph, count };
}

export function makeBifoldSpecimen(product, type, frame, materials) {
  const group = new THREE.Group(), materialByRole = { frame, thermal: materials.thermal, gasket: materials.gasket }, length = .38;
  const code = type === 'sill' ? BIFOLD_PARTS.sill : BIFOLD_PARTS.head;
  const track = new THREE.Group(), sash = new THREE.Group(), sashH = profiles[BIFOLD_PARTS.sash].height_mm / 1000;
  assemble(track, [[code, ['sill'], false]], length, .2, materialByRole);
  assemble(sash, [[BIFOLD_PARTS.sash, ['sill'], false]], length - .04, .2, materialByRole);
  if (type === 'head') { track.position.y = sashH; sash.position.y = .012; } else sash.position.y = SILL + .004;
  group.add(track, sash);
  group.userData.profileCode = profiles[code].code; group.userData.trackCount = 0; group.userData.profileDepth = profiles[code].depth_mm / 1000;
  group.traverse(o => { if (o.isMesh) o.userData.productId = product.id; });
  return group;
}
